import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent, type TouchEvent, type WheelEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../auth";
import { AppHeader } from "./AppHeader";
import {
  buildPhotoViewerUrl,
  anchorWheelZoomToPoint,
  clampPanOffset,
  getPhotoSwipeDirection,
  type PanLayoutMetrics,
} from "./photoViewerUtils";
import { ProtectedImage, protectedPhotoQueryOptions } from "./ProtectedImage";

const NAV_HIDE_DELAY_MS = 2500;
const ZOOM_WHEEL_EXP_FACTOR = 0.0009;
const ZOOM_PINCH_WHEEL_EXP_FACTOR = 0.0045;
const ZOOM_PINCH_EXPONENT = 1.35;
const ZOOM_WHEEL_SETTLE_MS = 500;
const ZOOM_BUTTON_STEP = 0.15;

function isEditableTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && Boolean(target.closest("input, textarea, select, [contenteditable='true']"));
}

export function PhotoViewerScreen({
  photoId,
  photoIds = [],
  photoIndex = 0,
  backTo = "/app/match",
}: {
  photoId: string;
  photoIds?: string[];
  photoIndex?: number;
  backTo?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const layoutMetricsRef = useRef<PanLayoutMetrics | null>(null);
  const zoomOriginRef = useRef("center center");
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [zoomOrigin, setZoomOrigin] = useState("center center");
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const swipeRef = useRef<{ startX: number; startY: number; x: number; y: number } | null>(null);
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const hideNavTimeoutRef = useRef<number | null>(null);
  const navHoverRef = useRef(false);
  const prevNavRef = useRef<HTMLButtonElement | null>(null);
  const nextNavRef = useRef<HTMLButtonElement | null>(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const wheelGestureActiveRef = useRef(false);
  const wheelAnchorTimeoutRef = useRef<number | null>(null);
  const [navVisible, setNavVisible] = useState(false);
  const [wheelZooming, setWheelZooming] = useState(false);
  const canNavigate = photoIds.length > 1;

  const clearHideNavTimeout = useCallback(() => {
    if (hideNavTimeoutRef.current !== null) {
      window.clearTimeout(hideNavTimeoutRef.current);
      hideNavTimeoutRef.current = null;
    }
  }, []);

  const showNav = useCallback(() => {
    setNavVisible(true);
  }, []);

  const hideNavSoon = useCallback(() => {
    if (navHoverRef.current) return;
    clearHideNavTimeout();
    hideNavTimeoutRef.current = window.setTimeout(() => {
      if (navHoverRef.current) return;
      setNavVisible(false);
      hideNavTimeoutRef.current = null;
    }, NAV_HIDE_DELAY_MS);
  }, [clearHideNavTimeout]);

  const isPointerOverNav = useCallback((clientX: number, clientY: number) => {
    for (const button of [prevNavRef.current, nextNavRef.current]) {
      if (!button) continue;
      const { left, right, top, bottom } = button.getBoundingClientRect();
      if (clientX >= left && clientX <= right && clientY >= top && clientY <= bottom) {
        return true;
      }
    }
    return false;
  }, []);

  const syncNavHoverFromPointer = useCallback(() => {
    const { x, y } = pointerRef.current;
    const overNav = isPointerOverNav(x, y)
      || Boolean(document.elementFromPoint(x, y)?.closest(".photo-viewer-nav"));
    navHoverRef.current = overNav;
    if (overNav) {
      showNav();
      clearHideNavTimeout();
    }
    return overNav;
  }, [clearHideNavTimeout, isPointerOverNav, showNav]);

  const clearWheelAnchor = useCallback(() => {
    wheelGestureActiveRef.current = false;
    setWheelZooming(false);
    if (wheelAnchorTimeoutRef.current !== null) {
      window.clearTimeout(wheelAnchorTimeoutRef.current);
      wheelAnchorTimeoutRef.current = null;
    }
  }, []);

  const scheduleWheelAnchorRelease = useCallback(() => {
    if (wheelAnchorTimeoutRef.current !== null) {
      window.clearTimeout(wheelAnchorTimeoutRef.current);
    }
    wheelAnchorTimeoutRef.current = window.setTimeout(() => {
      wheelGestureActiveRef.current = false;
      setWheelZooming(false);
      wheelAnchorTimeoutRef.current = null;
    }, ZOOM_WHEEL_SETTLE_MS);
  }, []);

  const isPointerOverImage = useCallback((clientX: number, clientY: number) => {
    const img = imgRef.current;
    if (!img) return false;
    const { left, right, top, bottom } = img.getBoundingClientRect();
    return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
  }, []);

  const revealNav = useCallback(() => {
    showNav();
    hideNavSoon();
  }, [hideNavSoon, showNav]);

  const measureLayout = useCallback(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    const containerRect = container.getBoundingClientRect();
    const previous = layoutMetricsRef.current;
    const next: PanLayoutMetrics = {
      imgWidth: img.offsetWidth,
      imgHeight: img.offsetHeight,
      containerWidth: containerRect.width,
      containerHeight: containerRect.height,
      layoutLeft: previous?.layoutLeft ?? 0,
      layoutTop: previous?.layoutTop ?? 0,
    };

    if (scaleRef.current <= 1) {
      const imgRect = img.getBoundingClientRect();
      next.layoutLeft = imgRect.left - containerRect.left;
      next.layoutTop = imgRect.top - containerRect.top;
    }

    layoutMetricsRef.current = next;
  }, []);

  const clampOffset = useCallback((next: { x: number; y: number }, nextScale = scaleRef.current, origin = zoomOriginRef.current) => (
    clampPanOffset(next, nextScale, origin, layoutMetricsRef.current)
  ), []);

  const applyClampedOffset = useCallback((updater: { x: number; y: number } | ((current: { x: number; y: number }) => { x: number; y: number })) => {
    setOffset((current) => {
      const raw = typeof updater === "function" ? updater(current) : updater;
      return clampOffset(raw);
    });
  }, [clampOffset]);

  useLayoutEffect(() => {
    zoomOriginRef.current = zoomOrigin;
  }, [zoomOrigin]);

  useLayoutEffect(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    setZoomOrigin("center center");
    zoomOriginRef.current = "center center";
    clearWheelAnchor();
    if (navHoverRef.current) {
      showNav();
      clearHideNavTimeout();
    }
  }, [clearHideNavTimeout, clearWheelAnchor, showNav, photoId]);

  useLayoutEffect(() => {
    if (scale <= 1) return;
    applyClampedOffset((current) => current);
  }, [applyClampedOffset, scale, zoomOrigin]);

  useEffect(() => {
    const onResize = () => {
      measureLayout();
      if (scaleRef.current > 1) {
        applyClampedOffset((current) => current);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [applyClampedOffset, measureLayout]);

  useLayoutEffect(() => {
    if (scale > 1) return;
    measureLayout();
  }, [measureLayout, scale, photoId]);

  scaleRef.current = scale;
  offsetRef.current = offset;

  const goToPhoto = useCallback((nextIndex: number) => {
    const nextPhotoId = photoIds[nextIndex];
    if (!nextPhotoId) return;
    navigate(buildPhotoViewerUrl(photoIds, nextPhotoId), { replace: true, state: { from: backTo } });
  }, [backTo, navigate, photoIds]);

  useEffect(() => {
    if (!user?.id || photoIds.length < 2) return;
    const adjacentIds = [
      photoIds[(photoIndex - 1 + photoIds.length) % photoIds.length],
      photoIds[(photoIndex + 1) % photoIds.length],
    ];
    for (const adjacentId of new Set(adjacentIds)) {
      void queryClient.prefetchQuery(protectedPhotoQueryOptions(user.id, adjacentId, "full"));
    }
  }, [photoIds, photoIndex, queryClient, user?.id]);

  useEffect(() => {
    if (!canNavigate) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (isEditableTarget(event.target)) return;

      event.preventDefault();
      const delta = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (photoIndex + delta + photoIds.length) % photoIds.length;
      goToPhoto(nextIndex);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canNavigate, goToPhoto, photoIndex, photoIds.length]);

  useEffect(() => () => {
    clearHideNavTimeout();
    clearWheelAnchor();
  }, [clearHideNavTimeout, clearWheelAnchor]);

  useEffect(() => {
    const frame = containerRef.current?.closest<HTMLElement>(".phone-frame");
    if (!frame) return;
    const preventFrameScroll = (event: Event) => {
      const wheel = event as globalThis.WheelEvent;
      if (!wheelGestureActiveRef.current && !isPointerOverImage(wheel.clientX, wheel.clientY)) return;
      event.preventDefault();
    };
    frame.addEventListener("wheel", preventFrameScroll, { passive: false });
    return () => frame.removeEventListener("wheel", preventFrameScroll);
  }, [isPointerOverImage]);

  const goToPrevious = useCallback(() => {
    goToPhoto((photoIndex - 1 + photoIds.length) % photoIds.length);
  }, [goToPhoto, photoIndex, photoIds.length]);

  const goToNext = useCallback(() => {
    goToPhoto((photoIndex + 1) % photoIds.length);
  }, [goToPhoto, photoIndex, photoIds.length]);

  const onViewerMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (!canNavigate) return;
    revealNav();
  };

  const onViewerMouseLeave = (event: MouseEvent<HTMLDivElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    window.requestAnimationFrame(() => {
      if (syncNavHoverFromPointer()) return;
      clearHideNavTimeout();
      setNavVisible(false);
    });
  };

  const onNavClick = (event: MouseEvent<HTMLButtonElement>, go: () => void) => {
    event.stopPropagation();
    pointerRef.current = { x: event.clientX, y: event.clientY };
    navHoverRef.current = true;
    clearHideNavTimeout();
    showNav();
    go();
  };

  const onNavEnter = (event: MouseEvent<HTMLButtonElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    navHoverRef.current = true;
    clearHideNavTimeout();
    showNav();
  };

  const onNavLeave = (event: MouseEvent<HTMLButtonElement>) => {
    pointerRef.current = { x: event.clientX, y: event.clientY };
    window.requestAnimationFrame(() => {
      if (!syncNavHoverFromPointer()) hideNavSoon();
    });
  };

  const clampScale = (value: number) => Math.min(4, Math.max(1, value));

  const setZoomScale = useCallback((updater: number | ((current: number) => number)) => {
    setZoomOrigin("center center");
    zoomOriginRef.current = "center center";
    setScale((current) => {
      const raw = typeof updater === "function" ? updater(current) : updater;
      const next = clampScale(raw);
      if (next <= 1) {
        setOffset({ x: 0, y: 0 });
      } else if (next < current) {
        const ratio = next / current;
        setOffset((offset) => clampOffset({
          x: offset.x * ratio,
          y: offset.y * ratio,
        }, next, "center center"));
      } else if (next > current) {
        setOffset((offset) => clampOffset(offset, next, "center center"));
      }
      return next;
    });
  }, [clampOffset]);

  const onWheel = (event: WheelEvent<HTMLDivElement>) => {
    const overImg = isPointerOverImage(event.clientX, event.clientY);
    if (!wheelGestureActiveRef.current && !overImg) return;

    event.preventDefault();
    const img = imgRef.current;
    if (!img) return;

    const wheelFactor = event.ctrlKey ? ZOOM_PINCH_WHEEL_EXP_FACTOR : ZOOM_WHEEL_EXP_FACTOR;
    const factor = Math.exp(-event.deltaY * wheelFactor);

    if (!wheelGestureActiveRef.current) {
      wheelGestureActiveRef.current = true;
      setWheelZooming(true);
      const layout = layoutMetricsRef.current;
      const container = containerRef.current;
      if (layout && container) {
        const containerRect = container.getBoundingClientRect();
        const anchor = anchorWheelZoomToPoint({
          clientX: event.clientX,
          clientY: event.clientY,
          scale: scaleRef.current,
          offset: offsetRef.current,
          zoomOrigin: zoomOriginRef.current,
          layout,
          containerLeft: containerRect.left,
          containerTop: containerRect.top,
        });
        const nextOffset = clampOffset(anchor.offset, scaleRef.current, anchor.origin);
        zoomOriginRef.current = anchor.origin;
        offsetRef.current = nextOffset;
        setZoomOrigin(anchor.origin);
        setOffset(nextOffset);
      }
    }
    scheduleWheelAnchorRelease();

    let zoomedOut = false;
    setScale((oldScale) => {
      const newScale = clampScale(oldScale * factor);
      if (newScale <= 1) {
        zoomedOut = true;
        return 1;
      }
      return newScale;
    });
    if (zoomedOut) {
      setZoomOrigin("center center");
      zoomOriginRef.current = "center center";
      setOffset({ x: 0, y: 0 });
      clearWheelAnchor();
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLImageElement>) => {
    if (scale <= 1) return;
    dragRef.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLImageElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    applyClampedOffset({
      x: drag.ox + (event.clientX - drag.x),
      y: drag.oy + (event.clientY - drag.y),
    });
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    swipeRef.current = null;
    if (event.touches.length === 1 && canNavigate && scaleRef.current <= 1) {
      const touch = event.touches[0];
      swipeRef.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        x: touch.clientX,
        y: touch.clientY,
      };
      revealNav();
      return;
    }

    if (event.touches.length !== 2) return;
    const [a, b] = [event.touches[0], event.touches[1]];
    const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const centerX = (a.clientX + b.clientX) / 2;
    const centerY = (a.clientY + b.clientY) / 2;
    const layout = layoutMetricsRef.current;
    const container = containerRef.current;
    if (layout && container) {
      const containerRect = container.getBoundingClientRect();
      const anchor = anchorWheelZoomToPoint({
        clientX: centerX,
        clientY: centerY,
        scale: scaleRef.current,
        offset: offsetRef.current,
        zoomOrigin: zoomOriginRef.current,
        layout,
        containerLeft: containerRect.left,
        containerTop: containerRect.top,
      });
      const nextOffset = clampOffset(anchor.offset, scaleRef.current, anchor.origin);
      zoomOriginRef.current = anchor.origin;
      offsetRef.current = nextOffset;
      setZoomOrigin(anchor.origin);
      setOffset(nextOffset);
    }
    pinchRef.current = { distance, scale: scaleRef.current };
  };

  const onTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const pinch = pinchRef.current;
    if (pinch && event.touches.length === 2) {
      event.preventDefault();
      const [a, b] = [event.touches[0], event.touches[1]];
      const distance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const pinchRatio = distance / pinch.distance;
      const nextScale = clampScale(pinch.scale * pinchRatio ** ZOOM_PINCH_EXPONENT);

      if (nextScale <= 1) {
        setZoomOrigin("center center");
        zoomOriginRef.current = "center center";
        offsetRef.current = { x: 0, y: 0 };
        setOffset({ x: 0, y: 0 });
        setScale(1);
        return;
      }

      setScale(nextScale);
      applyClampedOffset((current) => current);
      return;
    }

    const swipe = swipeRef.current;
    if (!swipe || event.touches.length !== 1) return;
    const touch = event.touches[0];
    swipe.x = touch.clientX;
    swipe.y = touch.clientY;
    if (Math.abs(swipe.x - swipe.startX) > Math.abs(swipe.y - swipe.startY) && event.cancelable) {
      event.preventDefault();
    }
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (pinchRef.current) {
      pinchRef.current = null;
      swipeRef.current = null;
      setZoomScale((current) => (current <= 1.02 ? 1 : current));
      return;
    }

    const swipe = swipeRef.current;
    swipeRef.current = null;
    const touch = event.changedTouches[0];
    if (!swipe || !touch || scaleRef.current > 1) return;

    const direction = getPhotoSwipeDirection({
      deltaX: touch.clientX - swipe.startX,
      deltaY: touch.clientY - swipe.startY,
      viewportWidth: containerRef.current?.clientWidth || window.innerWidth,
    });
    if (direction === "next") goToNext();
    if (direction === "previous") goToPrevious();
  };

  const onTouchCancel = () => {
    pinchRef.current = null;
    swipeRef.current = null;
  };

  return (
    <>
      <AppHeader
        title={canNavigate ? `Photo ${photoIndex + 1} of ${photoIds.length}` : "Photo"}
        backTo={backTo}
      />
      <div
        className={`photo-viewer photo-viewer--interactive${navVisible ? " photo-viewer--nav-visible" : ""}`}
        ref={containerRef}
        onMouseMove={onViewerMouseMove}
        onMouseLeave={onViewerMouseLeave}
        onWheel={onWheel}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
      >
        {canNavigate && (
          <>
            <button
              ref={prevNavRef}
              type="button"
              className="photo-viewer-nav photo-viewer-nav--prev"
              aria-label="Previous photo"
              onMouseEnter={onNavEnter}
              onMouseLeave={onNavLeave}
              onClick={(event) => onNavClick(event, goToPrevious)}
            >
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
            <button
              ref={nextNavRef}
              type="button"
              className="photo-viewer-nav photo-viewer-nav--next"
              aria-label="Next photo"
              onMouseEnter={onNavEnter}
              onMouseLeave={onNavLeave}
              onClick={(event) => onNavClick(event, goToNext)}
            >
              <ChevronRight size={22} aria-hidden="true" />
            </button>
          </>
        )}
        <div className="photo-viewer-stage">
          {photoId ? (
            <ProtectedImage
              ref={imgRef}
              photoId={photoId}
              variant="full"
              loading="eager"
              alt={canNavigate ? `Photo ${photoIndex + 1} of ${photoIds.length}` : ""}
              className={wheelZooming ? "photo-viewer-img--wheel-zoom" : undefined}
              style={{
                transformOrigin: zoomOrigin,
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              onLoad={measureLayout}
              draggable={false}
            />
          ) : null}
        </div>
      </div>
      {photoId && (
        <div className="photo-viewer-toolbar" role="toolbar" aria-label="Photo zoom controls">
          <button
            type="button"
            className="photo-viewer-tool"
            onClick={() => setZoomScale((current) => current - ZOOM_BUTTON_STEP)}
            aria-label="Zoom out"
          >
            <ZoomOut size={18} aria-hidden="true" />
          </button>
          <span className="photo-viewer-toolbar-divider" aria-hidden="true" />
          <button
            type="button"
            className="photo-viewer-tool"
            onClick={() => setZoomScale((current) => current + ZOOM_BUTTON_STEP)}
            aria-label="Zoom in"
          >
            <ZoomIn size={18} aria-hidden="true" />
          </button>
        </div>
      )}
    </>
  );
}
